// Globals
import { ReactNode, useCallback, useEffect, useMemo, useState } from 'react';

// Helpers
import {SocketContext, TButton} from '../../helpers';
import { generateId, getUserId } from '../../helpers';

// Models
import { TFormFields, TSendFormDTO, TSendMessageDTO } from '@/models/chat.model';

// Modules
import {useMessages, useSocketIO} from "@/modules/Socket/hooks";
import { TUserDataFormValues, UserDataFormFields } from '@/modules/Chat';

type TChatProviderProps = {
  children?: ReactNode;
};

const SocketContainer = ({ children }: TChatProviderProps) => {
  const [isTyping, setIsTyping] = useState(false);
  const { messages, setMessages } = useMessages();
  const [buttons, setButtons] = useState<TButton[]>([]);
  const [formFields, setFormFields] = useState<TFormFields[]>([]);
  const [userId] = useState(() => getUserId() ?? generateId());

  const { sendMessageAI, sendFormAI, handleCheckForm, clearForm } = useSocketIO({
    onConnect: () => {
      console.log('[socket] connected');
    },
    onDisconnect: () => {
      console.log('[socket] disconnected');
    },
    onMessageToUser: (message) => {
      setIsTyping(false);
      if (!message.message) return;
      setButtons([]);
      setMessages((prev) => [
        ...prev,
        {
          message: message.message,
          sender: 'AI',
          date: new Date(),
        },
      ]);
    },
    onFormToUser: (message) => {
      setIsTyping(false);
      if (!message.message) return;
      setFormFields(message.fields);
      setMessages((prev) => [
        ...prev,
        {
          message: message.message,
          sender: 'AI',
          date: new Date(),
        },
      ]);
    },
    onSelectToUser: (message) => {
      setIsTyping(false);
      if (!message.message) return;
      setButtons(message.options?.map(option => ({ text: option, action: option })))
      setMessages((prev) => [
        ...prev,
        {
          message: message.message,
          sender: 'AI',
          date: new Date(),
        },
      ]);
    },
    onErrorAI: (error) => {
      setIsTyping(false);
      setMessages((prev) => [
        ...prev,
        {
          message: `⚠️ AI error: ${error.message}`,
          sender: 'AI',
          date: new Date(),
        },
      ]);
    },
    onError: (error) => {
      console.error('[socket] general error:', error);
    },
  });

  const handleSendMessage = useCallback((message: string) => {
    if (!message.trim()) return;
    setMessages((prev) => [
      ...prev,
      {
        message,
        sender: 'USER',
        date: new Date(),
      },
    ]);
    setIsTyping(true);

    const payload: TSendMessageDTO = {
      message,
      sessionId: userId,
    };

    sendMessageAI(payload);
  }, [setMessages, userId, sendMessageAI]);

  const handleFormSubmit = useCallback((values: TUserDataFormValues) => {

    const payload: TSendFormDTO = {
      fields: {
        name: values[UserDataFormFields.NAME],
        email: values[UserDataFormFields.EMAIL],
        phone: values[UserDataFormFields.PHONE],
      },
      sessionId: userId,
    };
    setButtons([]);
    sendFormAI(payload);
    setFormFields([]);
  }, [userId, sendFormAI]);

  const handleClearForm = useCallback(() => {
    clearForm({ sessionId: userId});
    setFormFields([]);
  }, [userId, clearForm]);

  const value = useMemo(() => ({
    buttons,
    formFields,
    messages,
    isTyping,
    sendMessage: handleSendMessage,
    sendForm: handleFormSubmit,
    setMessages,
    clearForm: handleClearForm,
  }), [
    buttons,
    formFields,
    messages,
    isTyping,
    handleSendMessage,
    setMessages,
    handleClearForm,
    handleFormSubmit
  ]);

  useEffect(() => {
    if (userId) {
      handleCheckForm({ sessionId: userId});
    }
  }, [userId, handleCheckForm]);

  return (
      <SocketContext.Provider value={value}>
        {children}
      </SocketContext.Provider>
  );
};

export default SocketContainer;
